'use client';

import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Shield, Zap, Sparkles } from 'lucide-react';
import Image from 'next/image';

export function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex flex-col items-center"
        >
          {/* Main heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-6"
          >
            Yokki Bot
            <br />
            <span className="bg-gradient-to-r from-[#5865F2] via-[#7289da] to-[#5865F2] bg-clip-text text-transparent">
              Server Management
            </span>
          </motion.h1>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-xl sm:text-2xl text-[#b9bbbe] max-w-3xl mb-12"
          >
            Protect your community with advanced antinuke & antiraid security.
            Moderate effortlessly. Engage with fun games. All-in-one bot, 100% free.
          </motion.p>

          {/* CTA buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 mb-16"
          >
            <a href="https://discord.com/oauth2/authorize?client_id=1459634417306829056&permissions=8&integration_type=0&scope=bot+applications.commands" target="_blank" rel="noopener noreferrer">
              <Button
                size="lg"
                className="bg-[#5865F2] hover:bg-[#4752C4] text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg shadow-[#5865F2]/25 transition-all hover:shadow-xl hover:shadow-[#5865F2]/40 hover:scale-105"
              >
                <Shield className="mr-2 h-5 w-5" />
                Add to Discord
              </Button>
            </a>
            <a href="#commands">
              <Button
                size="lg"
                variant="outline"
                className="border-[#b9bbbe]/20 text-white hover:bg-[#252525] px-8 py-6 text-lg font-semibold rounded-lg"
              >
                View Commands
              </Button>
            </a>
          </motion.div>

          {/* Feature highlights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-4xl"
          >
            <div className="flex flex-col items-center">
              <div className="h-12 w-12 rounded-xl bg-[#5865F2]/10 flex items-center justify-center mb-3">
                <Shield className="h-6 w-6 text-[#5865F2]" />
              </div>
              <h3 className="text-white font-semibold mb-1">Great Protection</h3>
              <p className="text-[#b9bbbe] text-sm text-center">
                Advanced antinuke & antiraid
              </p>
            </div>

            <div className="flex flex-col items-center">
              <div className="h-12 w-12 rounded-xl bg-[#3ba55d]/10 flex items-center justify-center mb-3">
                <Zap className="h-6 w-6 text-[#3ba55d]" />
              </div>
              <h3 className="text-white font-semibold mb-1">Lightning Fast</h3>
              <p className="text-[#b9bbbe] text-sm text-center">
                Instant response times
              </p>
            </div>

            <div className="flex flex-col items-center">
              <div className="h-12 w-12 rounded-xl bg-[#5865F2]/10 flex items-center justify-center mb-3">
                <Sparkles className="h-6 w-6 text-[#5865F2]" />
              </div>
              <h3 className="text-white font-semibold mb-1">Easy Setup</h3>
              <p className="text-[#b9bbbe] text-sm text-center">
                Ready in under 2 minutes
              </p>
            </div>

            <div className="flex flex-col items-center">
              <div className="h-12 w-12 rounded-xl bg-[#3ba55d]/10 flex items-center justify-center mb-3">
                <Shield className="h-6 w-6 text-[#3ba55d]" />
              </div>
              <h3 className="text-white font-semibold mb-1">100% Free</h3>
              <p className="text-[#b9bbbe] text-sm text-center">
                No premium tiers
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0f0f0f] to-transparent z-5" />
    </section>
  );
}
